import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import confetti from "canvas-confetti";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ShareButton } from "@/components/ui/share-button";

const subscribeSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  role: z.enum(["fan", "artist", "sponsor"], {
    required_error: "Please select your role.",
  }),
});

type SubscribeFormValues = z.infer<typeof subscribeSchema>;

interface SubscriptionFormProps {
  onSuccess: () => void;
}

export function SubscriptionForm({ onSuccess }: SubscriptionFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<SubscribeFormValues>({
    resolver: zodResolver(subscribeSchema),
    defaultValues: {
      email: "",
    },
  });

  const triggerConfetti = () => {
    // Create a burst of confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });

    // Add some extra confetti bursts for more effect
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
      });
    }, 200);

    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
      });
    }, 400);
  };

  async function onSubmit(data: SubscribeFormValues) {
    setIsSubmitting(true);
    try {
      console.log("Attempting to subscribe:", data);
      const { error } = await supabase.from("early_access_subscribers").insert({
        email: data.email,
        role: data.role,
      });

      if (error) {
        console.error("Subscription error:", error);
        if (error.code === "23505") {
          toast.error("This email is already subscribed!");
        } else {
          throw error;
        }
      } else {
        console.log("Subscription successful");
        onSuccess();
        triggerConfetti();
        toast.success(
          "Thank you for subscribing! We'll notify you when we launch.",
        );
      }
    } catch (error: any) {
      console.error("Error subscribing:", error);
      toast.error(error.message || "Failed to subscribe. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">Email Address</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder="Enter your email"
                      type="email"
                      {...field}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50 pl-10 focus:bg-white/20 transition-colors"
                    />
                    <Mail className="absolute left-3 top-2.5 h-5 w-5 text-white/50" />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">I'm a...</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="grid grid-cols-3 gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="fan"
                        id="fan"
                        className="border-white/30 text-primary"
                      />
                      <label
                        htmlFor="fan"
                        className="text-white/80 text-sm font-medium cursor-pointer"
                      >
                        Fan
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="artist"
                        id="artist"
                        className="border-white/30 text-primary"
                      />
                      <label
                        htmlFor="artist"
                        className="text-white/80 text-sm font-medium cursor-pointer"
                      >
                        Artist
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="sponsor"
                        id="sponsor"
                        className="border-white/30 text-primary"
                      />
                      <label
                        htmlFor="sponsor"
                        className="text-white/80 text-sm font-medium cursor-pointer"
                      >
                        Sponsor
                      </label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full text-white font-semibold py-3 rounded-lg transition-all transform hover:scale-105"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Subscribing..." : "Notify Me When Ready"}
          </Button>
        </form>
      </Form>

      {/* Tell a Friend Share Section */}
      <div className="mt-8 pt-6 border-t border-white/20">
        <h3 className="text-white/90 text-lg font-semibold mb-3">
          Tell a Friend
        </h3>
        <p className="text-white/60 text-sm mb-4">
          Share PayStreem with your friends and help us build the future of
          music streaming together.
        </p>
        <div className="flex justify-center">
          <ShareButton
            url="https://www.paystreem.com"
            title="PayStreem - The Future of Music Streaming"
            text="🎵 Something amazing is coming to music streaming! PayStreem is launching soon - a revolutionary platform that connects artists, fans, and sponsors. Join the waitlist now!"
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          />
        </div>
      </div>

      <p className="text-white/50 text-xs mt-6">
        We'll only use your email to notify you about our launch. No spam, ever.
      </p>
    </>
  );
}
